import React, { useEffect, useMemo, useState } from 'react';

import { Card, Skeleton } from 'antd';
import type { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';

import { COLOR, FONT, RADIUS, SEMANTIC_COLOR, SPACE } from '@/shared/design-tokens/design-tokens';
import { formatHourMinute } from '@/shared/lib/format';
import { escapeHtml, formatMetricValue } from '@/shared/lib/utils';
import { Icon } from '@/shared/ui/Icon';

import { createTooltip } from '@/widgets/monitoring/shared/createTooltip';

import styles from './CompactDualMetricCard.module.css';

type PodHistory = {
  pod: string;
  data: { timestamp: number; value: number }[];
  color: string;
};

type CompactDualMetricCardProps = {
  title: string;
  primaryLabel: string;
  primaryValue: number;
  primaryHistory: { timestamp: number; value: number }[];
  primaryPodHistory: PodHistory[];
  primaryColor: string;
  secondaryLabel: string;
  secondaryValue: number;
  secondaryHistory: { timestamp: number; value: number }[];
  secondaryPodHistory: PodHistory[];
  secondaryColor: string;
  unit: string;
  isLoading?: boolean;
  icon?: React.ReactNode;
  onClick?: () => void;
};

type ChartType = 'line' | 'bar';

type TooltipItem = {
  axisValueLabel?: string;
  color: string;
  name: string;
  seriesName: string;
  value: number;
};

const CHART_HEIGHT = 220;
const METRIC_CARD_TOOLTIP_TEXT_COLOR = SEMANTIC_COLOR.ON_SURFACE.STANDARD.LEVEL1;

const createBaseAxisLabel = () => ({
  color: SEMANTIC_COLOR.ON_CONTAINER.STANDARD.LEVEL5,
  fontFamily: FONT.FAMILY.NANUMSQUARE,
  fontSize: FONT.SIZE.XS,
});

const getTooltipItemName = (item: TooltipItem, primaryLabel: string, secondaryLabel: string) => {
  if (item.seriesName.includes('-primary')) {
    return `${item.seriesName.replace('-primary', '')} (${primaryLabel})`;
  }

  if (item.seriesName.includes('-secondary')) {
    return `${item.seriesName.replace('-secondary', '')} (${secondaryLabel})`;
  }

  return item.seriesName || item.name;
};

const buildTooltipContent = (items: TooltipItem[], unit: string, primaryLabel: string, secondaryLabel: string) => {
  if (items.length === 0) {
    return '';
  }

  const title = items[0].axisValueLabel ?? items[0].name ?? '';
  const titleHtml =
    title &&
    `<div style="margin-bottom: ${SPACE[12]}px; font-family: ${FONT.FAMILY.NANUMSQUARE}; font-size: ${FONT.SIZE.SM}px; line-height: ${FONT.LINE_HEIGHT[16]}px; font-weight: ${FONT.WEIGHT.REGULAR}; color: ${SEMANTIC_COLOR.ON_SURFACE.STANDARD.LEVEL5}; letter-spacing: 0px;">${escapeHtml(title)}</div>`;

  const itemsHtml = items
    .map((item) => {
      return `
        <div style="display: flex; align-items: center; gap: ${SPACE[4]}px; height: ${SPACE[16]}px; margin-bottom: ${SPACE[4]}px;">
          <div style="
            width: ${SPACE[8]}px;
            height: ${SPACE[8]}px;
            margin: 0 ${SPACE[4]}px;
            border-radius: ${RADIUS.FULL}px;
            background-color: ${item.color || COLOR.GRAY[50]};
            flex-shrink: 0;
          "></div>
          <span style="
            font-family: ${FONT.FAMILY.NANUMSQUARE};
            font-size: ${FONT.SIZE.SM}px;
            line-height: ${FONT.LINE_HEIGHT[16]}px;
            font-weight: ${FONT.WEIGHT.REGULAR};
            color: ${METRIC_CARD_TOOLTIP_TEXT_COLOR};
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            flex: 1;
            min-width: 0;
          ">${getTooltipItemName(item, primaryLabel, secondaryLabel)}</span>
          <span style="
            font-family: ${FONT.FAMILY.NANUMSQUARE};
            font-size: ${FONT.SIZE.SM}px;
            line-height: ${FONT.LINE_HEIGHT[16]}px;
            font-weight: ${FONT.WEIGHT.BOLD};
            color: ${METRIC_CARD_TOOLTIP_TEXT_COLOR};
            margin-left: auto;
          ">${formatMetricValue(Math.abs(item.value))} ${unit}</span>
        </div>
      `;
    })
    .join('');

  return `<div style="width: 280px; max-height: 204px; overflow-y: hidden; box-sizing: border-box;">${titleHtml}<div style="display: flex; flex-direction: column; height: "176px"; gap: ${SPACE[4]}px;">${itemsHtml}</div></div>`;
};

const getPodColor = (podName: string, primaryPodHistory: PodHistory[], secondaryPodHistory: PodHistory[]) => {
  return (
    primaryPodHistory.find((pod) => pod.pod === podName)?.color ||
    secondaryPodHistory.find((pod) => pod.pod === podName)?.color ||
    COLOR.GRAY[50]
  );
};

export const CompactDualMetricCard = ({
  title,
  primaryLabel,
  primaryValue,
  primaryHistory: _primaryHistory,
  primaryPodHistory,
  primaryColor,
  secondaryLabel,
  secondaryValue,
  secondaryHistory: _secondaryHistory,
  secondaryPodHistory,
  secondaryColor,
  unit,
  isLoading = false,
  icon: _icon,
  onClick,
}: CompactDualMetricCardProps) => {
  const [selectedPods, setSelectedPods] = useState<Record<string, boolean>>({});
  const [chartType, setChartType] = useState<ChartType>('line');

  const allPods = useMemo(() => {
    const podSet = new Set<string>();
    primaryPodHistory.forEach((pod) => podSet.add(pod.pod));
    secondaryPodHistory.forEach((pod) => podSet.add(pod.pod));
    return Array.from(podSet);
  }, [primaryPodHistory, secondaryPodHistory]);

  const podNamesKey = useMemo(() => allPods.join(','), [allPods]);

  useEffect(() => {
    if (!podNamesKey) return;

    setSelectedPods((prev) => {
      const nextSelectedPods: Record<string, boolean> = {};

      podNamesKey
        .split(',')
        .filter(Boolean)
        .forEach((podName) => {
          nextSelectedPods[podName] = prev[podName] ?? true;
        });

      return nextSelectedPods;
    });
  }, [podNamesKey]);

  const filteredPods = useMemo(() => {
    return allPods.filter((podName) => selectedPods[podName] ?? true);
  }, [allPods, selectedPods]);

  const chartOption: EChartsOption = useMemo(() => {
    const hasPrimaryData = primaryPodHistory.length > 0;
    const hasSecondaryData = secondaryPodHistory.length > 0;

    if (!hasPrimaryData && !hasSecondaryData) {
      return {};
    }

    if (chartType === 'bar') {
      const latestPrimaryValues: Record<string, number> = {};
      const latestSecondaryValues: Record<string, number> = {};
      const podColors: Record<string, string> = {};

      filteredPods.forEach((podName) => {
        const primaryPod = primaryPodHistory.find((pod) => pod.pod === podName);
        const secondaryPod = secondaryPodHistory.find((pod) => pod.pod === podName);

        podColors[podName] = getPodColor(podName, primaryPodHistory, secondaryPodHistory);

        if (primaryPod && primaryPod.data.length > 0) {
          latestPrimaryValues[podName] = primaryPod.data[primaryPod.data.length - 1].value;
        }

        if (secondaryPod && secondaryPod.data.length > 0) {
          latestSecondaryValues[podName] = secondaryPod.data[secondaryPod.data.length - 1].value;
        }
      });

      return {
        animation: false,
        grid: {
          left: 0,
          right: 0,
          top: 8,
          bottom: 8,
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            show: false,
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'category',
          inverse: true,
          data: filteredPods,
          axisLine: {
            show: false,
          },
          axisTick: { show: false },
          axisLabel: {
            ...createBaseAxisLabel(),
            width: 98,
            overflow: 'truncate',
          },
        },
        series: [
          {
            name: primaryLabel,
            type: 'bar',
            data: filteredPods.map((podName) => ({
              value: latestPrimaryValues[podName] || 0,
              itemStyle: { color: podColors[podName], borderRadius: [0, 4, 4, 0] },
            })),
            barWidth: 7,
            barGap: '20%',
            barCategoryGap: '40%',
            emphasis: {
              disabled: true,
            },
          },
          {
            name: secondaryLabel,
            type: 'bar',
            data: filteredPods.map((podName) => ({
              value: latestSecondaryValues[podName] || 0,
              itemStyle: { color: podColors[podName], borderRadius: [0, 4, 4, 0] },
            })),
            barWidth: 7,
            barGap: '20%',
            barCategoryGap: '40%',
            emphasis: {
              disabled: true,
            },
          },
        ],
        tooltip: createTooltip(unit) as EChartsOption['tooltip'],
      };
    }

    const timeSource =
      primaryPodHistory.find((pod) => filteredPods.includes(pod.pod)) ||
      secondaryPodHistory.find((pod) => filteredPods.includes(pod.pod)) ||
      primaryPodHistory[0] ||
      secondaryPodHistory[0];

    const times = timeSource?.data.map((point) => formatHourMinute(point.timestamp)) ?? [];

    const series = filteredPods.flatMap((podName) => {
      const primaryPod = primaryPodHistory.find((pod) => pod.pod === podName);
      const secondaryPod = secondaryPodHistory.find((pod) => pod.pod === podName);
      const podColor = getPodColor(podName, primaryPodHistory, secondaryPodHistory);

      return [
        ...(primaryPod && primaryPod.data.length > 0
          ? [
              {
                id: `${podName}-primary`,
                name: `${podName}-primary`,
                type: 'line' as const,
                data: primaryPod.data.map((point) => -point.value),
                symbol: 'none' as const,
                showSymbol: false,
                smooth: false,
                connectNulls: false,
                hoverAnimation: false,
                clip: true,
                lineStyle: {
                  color: podColor,
                  width: 1.25,
                  cap: 'round' as const,
                  join: 'round' as const,
                },
                itemStyle: {
                  color: podColor,
                },
                emphasis: {
                  disabled: true,
                },
              },
            ]
          : []),
        ...(secondaryPod && secondaryPod.data.length > 0
          ? [
              {
                id: `${podName}-secondary`,
                name: `${podName}-secondary`,
                type: 'line' as const,
                data: secondaryPod.data.map((point) => point.value),
                symbol: 'none' as const,
                showSymbol: false,
                smooth: false,
                connectNulls: false,
                hoverAnimation: false,
                clip: true,
                lineStyle: {
                  color: podColor,
                  width: 1.25,
                  cap: 'round' as const,
                  join: 'round' as const,
                },
                itemStyle: {
                  color: podColor,
                },
                emphasis: {
                  disabled: true,
                },
              },
            ]
          : []),
      ];
    });

    return {
      animation: false,
      grid: {
        left: 0,
        right: 0,
        top: 8,
        bottom: 8,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: times,
        axisLine: {
          show: true,
          lineStyle: {
            color: SEMANTIC_COLOR.CONTAINER.STANDARD.ALT_LEVEL3,
          },
        },
        axisLabel: {
          ...createBaseAxisLabel(),
          align: 'left',
          color: SEMANTIC_COLOR.ON_CONTAINER.STANDARD.LEVEL5,
          fontFamily: FONT.FAMILY.NANUMSQUARE,
          fontSize: FONT.SIZE.SM,
          hideOverlap: false,
          margin: 12,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          ...createBaseAxisLabel(),
          margin: 12,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: SEMANTIC_COLOR.CONTAINER.STANDARD.ALT_LEVEL3,
            type: 'dashed',
            width: 1,
          },
        },
      },
      series,
      tooltip: {
        ...(createTooltip(unit) as EChartsOption['tooltip']),
        formatter: (params: unknown) => {
          return buildTooltipContent(params as TooltipItem[], unit, primaryLabel, secondaryLabel);
        },
      },
    };
  }, [chartType, filteredPods, primaryLabel, primaryPodHistory, secondaryLabel, secondaryPodHistory, unit]);

  const handleLegendClick = (podName: string) => {
    setSelectedPods((prev) => {
      const nextSelectedPods = {
        ...prev,
        [podName]: !(prev[podName] ?? true),
      };

      return nextSelectedPods;
    });
  };

  if (isLoading) {
    return (
      <Card className={styles.card}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    );
  }

  const chartHeight = chartType === 'bar' ? Math.max(CHART_HEIGHT, filteredPods.length * 28 + 36) : CHART_HEIGHT;

  const metricSummaries = [
    {
      key: primaryLabel,
      color: primaryColor,
      label: primaryLabel,
      value: primaryValue,
    },
    {
      key: secondaryLabel,
      color: secondaryColor,
      label: secondaryLabel,
      value: secondaryValue,
    },
  ];

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <span className={styles.title}>{title}</span>
          {onClick && (
            <button aria-label={`${title} 상세 보기`} className={styles.actionButton} onClick={onClick} type="button">
              <Icon className={styles.actionIcon} name="open-in-new" size={16} />
            </button>
          )}
        </div>

        <div aria-label={`${title} chart type`} className={styles.chartTypeGroup} role="group">
          <button
            aria-pressed={chartType === 'line'}
            aria-label={`${title} 선형 차트`}
            className={`${styles.chartTypeButton} ${chartType === 'line' ? styles.chartTypeButtonActive : ''}`}
            onClick={() => setChartType('line')}
            type="button">
            <Icon className={styles.chartTypeIcon} name="chart-line" size={16} />
          </button>
          <button
            aria-pressed={chartType === 'bar'}
            aria-label={`${title} 막대 차트`}
            className={`${styles.chartTypeButton} ${chartType === 'bar' ? styles.chartTypeButtonActive : ''}`}
            onClick={() => setChartType('bar')}
            type="button">
            <Icon className={styles.chartTypeIcon} name="chart-bar" size={16} />
          </button>
        </div>
      </div>

      <div className={styles.metricSummary}>
        {metricSummaries.map((metric) => (
          <div className={styles.metricGroup} key={metric.key}>
            <span className={styles.metricLabel}>{metric.label}</span>
            <span className={styles.metricValue} style={{ color: metric.color }}>
              {formatMetricValue(metric.value)}
            </span>
            <span className={styles.metricMeta}>{unit}</span>
          </div>
        ))}
      </div>

      <div className={`${styles.chart} ${chartType === 'bar' ? styles.chartBar : styles.chartLine}`}>
        <ReactECharts
          key={`compact-dual-${title}-${allPods.join(',')}-${chartType}`}
          lazyUpdate={true}
          notMerge={true}
          option={chartOption}
          opts={{ renderer: 'canvas' }}
          style={{ height: `${chartHeight}px`, width: '100%' }}
        />
      </div>

      <div className={styles.legendContainer}>
        {allPods.map((podName) => {
          const podColor = getPodColor(podName, primaryPodHistory, secondaryPodHistory);
          const isActive = selectedPods[podName] ?? true;

          return (
            <button
              aria-label={`${podName} 범례 토글`}
              aria-pressed={isActive}
              className={`${styles.legendItem} ${!isActive ? styles.legendItemInactive : ''}`}
              key={podName}
              onClick={() => handleLegendClick(podName)}
              type="button">
              <span className={styles.legendMarkerWrapper}>
                <span className={styles.legendMarker} style={{ backgroundColor: podColor }} />
              </span>
              <span className={styles.legendText} title={podName}>
                {podName}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
